// src/utils/jobReminders.js
// ===========================================================================
// Job reminders: clean-up lists that work like Missed Jobs.
//
//   proposalJob  a job just made from an approved proposal (or a paid
//                deposit) that still needs its date and techs. Set the date,
//                techs and job description right in the reminder.
//   dupWO      one WO number used at two or more properties (Admin / Manager):
//              give one property a new number everywhere, right in the row
//   noTech     a job with nobody on Tech Assign
//   clockedIn  someone clocked in on a day but isn't on any job running that day
//   subPay     a Sub Contractor is on a one-time job, but no pay is set for them
//              (no Daily/Project term and no owed line) - Admin only
//
// Home shows recent ones (popup + banner, "remind me tomorrow").
// Job History -> Reminders shows every job, so old jobs can be corrected.
//
// DISMISS is permanent and shared by everyone:
//   jobReminderDismissals/{kind}__{key}  { kind, key, jobId, label, reason, by, at }
//   key = jobId (noTech), userId__day (clockedIn), jobId__subKey (subPay)
// A dismissed reminder never comes back for that job / person-day / sub.
//
// The "Later" snooze is per person, per browser, until tomorrow (same as
// Missed Jobs).
// ===========================================================================

import moment from "moment";
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getOccurrencesForJob } from "./getOccurrencesForJob";
import { isRecurringJob, lineOwed } from "./subPayTerms";

export const DISMISS_COLLECTION = "jobReminderDismissals";
const DAY = "YYYY-MM-DD";

export const REMINDER_KINDS = {
  proposalJob: {
    id: "proposalJob",
    icon: "📋",
    title: "New jobs from approved proposals",
    short: "New proposal jobs",
    banner: (n) => `${n} new job${n === 1 ? "" : "s"} from approved proposals need${n === 1 ? "s" : ""} a date and techs`,
    dismissReasons: ["Scheduled another way", "Job is on hold", "Customer cancelled", "Other"],
  },
  dupWO: {
    id: "dupWO",
    icon: "🔁",
    title: "Same WO number at more than one property",
    short: "Duplicate WO",
    banner: (n) => `${n} work order number${n === 1 ? " is" : "s are"} used at more than one property`,
    dismissReasons: ["Same customer, two property names", "Intended - linked work", "Other"],
  },
  noTech: {
    id: "noTech",
    icon: "👷",
    title: "Jobs with no tech assigned",
    short: "No tech",
    banner: (n) => `${n} job${n === 1 ? " has" : "s have"} no tech assigned`,
    dismissReasons: ["No tech needed", "Office / owner handled it", "Sub-contracted out", "Job was cancelled", "Other"],
  },
  clockedIn: {
    id: "clockedIn",
    icon: "⏱",
    title: "Clocked in, not on a job",
    short: "Clocked in",
    banner: (n) => `${n} clock-in${n === 1 ? "" : "s"} not assigned to any job`,
    dismissReasons: ["Shop / warehouse work", "Office work", "Training", "Errands / supply run", "Other"],
  },
  subPay: {
    id: "subPay",
    icon: "💵",
    title: "Contractor pay not set",
    short: "Contractor pay",
    banner: (n) => `${n} job${n === 1 ? " needs" : "s need"} contractor pay added`,
    dismissReasons: ["Paid another way", "No pay owed", "Included in another job", "Other"],
  },
};

/* ------------------------------------------------------------------ */
/* small helpers                                                       */
/* ------------------------------------------------------------------ */

export const todayKey = () => moment().format(DAY);

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === "object" && typeof v.seconds === "number") return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const dayKeyOf = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = toDate(v);
  return d ? moment(d).format(DAY) : "";
};

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

/** techAssign is an array on newer jobs, a (comma separated) string on older ones. */
export const techList = (ta) => {
  if (Array.isArray(ta)) return ta.map((t) => String(t || "").trim()).filter(Boolean);
  if (typeof ta === "string") return ta.split(/[,;/]|\band\b|&/i).map((t) => t.trim()).filter(Boolean);
  return [];
};

export const personName = (u = {}) =>
  `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.displayName || u.name || u.fullName || u.email || "";

const namesForPerson = (user = {}, log = {}) =>
  Array.from(
    new Set(
      [`${user.firstName || ""} ${user.lastName || ""}`, user.displayName, user.name, user.fullName, user.nickname, log.userName]
        .map(norm)
        .filter(Boolean)
    )
  );

const CLOSED_STATUSES = new Set(["deleted", "cancelled", "canceled", "void", "voided"]);
const HIDDEN_SERVICE_TYPES = new Set(["Approved Proposal", "Denied Proposal"]);
const isLiveJob = (job = {}) =>
  !CLOSED_STATUSES.has(String(job.status || "").trim().toLowerCase()) && !HIDDEN_SERVICE_TYPES.has(job.serviceType);

/** Does the job run on this day? (one-time: start..end, recurring: an occurrence) */
export const jobRunsOn = (job, day) => {
  const start = dayKeyOf(job.startDate);
  if (!start) return false;
  if (isRecurringJob(job)) {
    try {
      const m = moment(day, DAY);
      if (getOccurrencesForJob(job, m, m).includes(day)) return true;
    } catch {
      /* fall through */
    }
    // Moved visits live in occurrenceOverrides.
    return Object.values(job.occurrenceOverrides || {}).some((o) => dayKeyOf(o?.startDate || o?.occurrenceDate) === day);
  }
  const end = dayKeyOf(job.endDate) || start;
  const [a, b] = start <= end ? [start, end] : [end, start];
  return day >= a && day <= b;
};

/** Techs on the job that day (a recurring visit can have its own list). */
const techsOnDay = (job, day) => {
  const override = job.occurrenceOverrides?.[day];
  if (override && override.techAssign !== undefined) return techList(override.techAssign);
  return techList(job.techAssign);
};

const jobLabel = (job = {}) => [job.wo ? `WO ${job.wo}` : "", job.siteName || job.propertyName || ""].filter(Boolean).join(" · ") || job.id;

/** First and last day a job is on the calendar inside [from, to] (or overall). */
const jobDateInfo = (job, from, to) => {
  const start = dayKeyOf(job.startDate);
  const end = dayKeyOf(job.endDate) || start;
  if (!isRecurringJob(job)) return { start, end, inRange: !from || (start && end >= from && start <= to) };
  if (!from) return { start, end, inRange: true };
  let days = [];
  try {
    days = getOccurrencesForJob(job, moment(from, DAY), moment(to, DAY));
  } catch {
    days = [];
  }
  return { start: days[0] || start, end: days[days.length - 1] || end, inRange: days.length > 0, nextDay: days.find((d) => d >= todayKey()) };
};

/* ------------------------------------------------------------------ */
/* dismissals                                                          */
/* ------------------------------------------------------------------ */

export const dismissDocId = (kind, key) => `${kind}__${String(key).replace(/\//g, "-")}`.slice(0, 1400);

export const loadDismissals = async () => {
  const snap = await getDocs(collection(db, DISMISS_COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const dismissedSet = (dismissals = []) => new Set(dismissals.map((d) => dismissDocId(d.kind, d.key)));

export const dismissReminder = async (row, { reason = "", note = "", by = "", byUid = "" } = {}) => {
  const payload = {
    kind: row.kind,
    key: row.key,
    jobId: row.jobId || "",
    label: row.label || "",
    reason,
    note,
    by,
    byUid,
    at: serverTimestamp(),
  };
  await setDoc(doc(db, DISMISS_COLLECTION, dismissDocId(row.kind, row.key)), payload);
  return { ...payload, at: new Date() };
};

export const undoDismiss = (kind, key) => deleteDoc(doc(db, DISMISS_COLLECTION, dismissDocId(kind, key)));

/* ------------------------------------------------------------------ */
/* row builders (pure)                                                  */
/* ------------------------------------------------------------------ */

const siteKeyOf = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * One row per WO number (any upper/lower case) whose records sit at two or
 * more properties. records: [{ collection, id, wo, siteName, scope, status }]
 * from jobs, addItem, approvedInvoices and proposals.
 */
export const buildDupWORows = ({ records = [], dismissed = new Set() } = {}) => {
  const byWO = new Map();
  records.forEach((r) => {
    const wo = String(r.wo ?? "").trim();
    if (!wo || !siteKeyOf(r.siteName)) return;
    const k = wo.toUpperCase();
    if (!byWO.has(k)) byWO.set(k, []);
    byWO.get(k).push({ ...r, wo });
  });
  const out = [];
  byWO.forEach((list, k) => {
    const sites = new Map();
    list.forEach((r) => {
      const sk = siteKeyOf(r.siteName);
      if (!sites.has(sk)) sites.set(sk, { siteName: r.siteName, records: [] });
      sites.get(sk).records.push(r);
    });
    if (sites.size < 2) return;
    if (dismissed.has(dismissDocId("dupWO", k))) return;
    const siteList = [...sites.values()].map((s0) => ({
      ...s0,
      kinds: Array.from(new Set(s0.records.map((r) => r.collection))),
      scope: s0.records.find((r) => r.scope)?.scope || "",
      // newest record first, so the later (usually wrong) one is easy to spot
      latest: s0.records.map((r) => r.createdKey || "").sort().pop() || "",
    }));
    out.push({
      kind: "dupWO",
      key: k,
      jobId: "",
      label: `WO ${list[0].wo}`,
      wo: list[0].wo,
      siteName: siteList.map((x) => x.siteName).join(" · "),
      sites: siteList,
      day: siteList.map((x) => x.latest).sort().pop() || "",
    });
  });
  return out.sort((a, b) => String(b.wo).localeCompare(String(a.wo), undefined, { numeric: true }));
};

/** A job that came straight out of an approved proposal (not a Continue of one). */
export const isNewProposalJob = (job = {}) =>
  Boolean(job.proposalId || job.contractBilling?.source === "proposal-deposit") &&
  !job.continuedFromJobId &&
  !job.projectContinuedFromJobId &&
  !job.createdByContinue &&
  !job.createdByProjectContinue;

/**
 * Jobs made from an approved proposal that still need a date or techs.
 * Gone once both are set (from here or anywhere else in the app), or once
 * someone saves the setup in the reminder (proposalSetupAt).
 * createdSince (YYYY-MM-DD): only jobs created on/after it (Home); empty = all.
 */
export const buildProposalJobRows = ({ jobs = [], dismissed = new Set(), createdSince = "" } = {}) => {
  const out = [];
  jobs.forEach((job) => {
    if (!isLiveJob(job) || !isNewProposalJob(job)) return;
    if (String(job.status || "").trim() !== "Active") return;
    if (job.proposalSetupAt) return;
    const start = dayKeyOf(job.startDate);
    const techs = techList(job.techAssign);
    if (start && techs.length) return;
    const created = dayKeyOf(job.createdAt || job.creationDate);
    if (createdSince && created && created < createdSince) return;
    const key = job.id;
    if (dismissed.has(dismissDocId("proposalJob", key))) return;
    out.push({
      kind: "proposalJob",
      key,
      jobId: job.id,
      job,
      label: jobLabel(job),
      wo: job.wo || "",
      siteName: job.siteName || job.propertyName || "",
      city: job.city || "",
      serviceType: job.serviceType || "",
      status: job.status || "",
      scope: job.scope || "",
      // "day" drives the age filters; the created day is the useful one here.
      day: created || start || "",
      created,
      missing: [!start ? "date" : "", !techs.length ? "techs" : ""].filter(Boolean),
      fromDeposit: job.contractBilling?.source === "proposal-deposit",
    });
  });
  return out.sort((a, b) => String(b.day).localeCompare(String(a.day)));
};

/**
 * Save the setup from the reminder: date(s), techs, job description.
 * Returns the patch for local state.
 */
export const saveProposalJobSetup = async (job, { startDate, endDate, techAssign = [], jobDescription, by = "" }) => {
  if (!job?.id) throw new Error("No job.");
  const start = dayKeyOf(startDate);
  if (!start) throw new Error("Pick a start date.");
  const end = dayKeyOf(endDate) || start;
  const techs = Array.from(new Set(techList(techAssign)));
  const patch = {
    startDate: start,
    endDate: end < start ? start : end,
    techAssign: techs,
    jobDescription: String(jobDescription ?? job.jobDescription ?? ""),
    proposalSetupAt: new Date().toISOString(),
    proposalSetupBy: by,
    updatedAt: new Date().toISOString(),
  };
  // Construction jobs usually run all day; keep a time that is already set.
  if (!job.startTime) patch.startTime = "ANY";
  if (!job.recurrence?.type) patch.recurrence = { type: "none" };
  await updateDoc(doc(db, "jobs", job.id), patch);
  return patch;
};

/**
 * Jobs with nobody on Tech Assign.
 * from/to (YYYY-MM-DD) limit to jobs on the calendar in that window; leave
 * both empty for every job (Job History).
 */
export const buildNoTechRows = ({ jobs = [], dismissed = new Set(), from = "", to = "" } = {}) => {
  const out = [];
  jobs.forEach((job) => {
    if (!isLiveJob(job)) return;
    if (techList(job.techAssign).length) return;
    const info = jobDateInfo(job, from, to);
    if (from && !info.inRange) return;
    const key = job.id;
    if (dismissed.has(dismissDocId("noTech", key))) return;
    const showDay = info.nextDay || info.start || "";
    out.push({
      kind: "noTech",
      key,
      jobId: job.id,
      job,
      label: jobLabel(job),
      wo: job.wo || "",
      siteName: job.siteName || job.propertyName || "",
      city: job.city || "",
      serviceType: job.serviceType || "",
      status: job.status || "",
      day: showDay,
      endDay: info.end && info.end !== showDay ? info.end : "",
      recurring: isRecurringJob(job),
      past: showDay && showDay < todayKey(),
      noDate: !showDay,
    });
  });
  return out.sort((a, b) => {
    if (a.noDate !== b.noDate) return a.noDate ? 1 : -1;
    return String(b.day).localeCompare(String(a.day));
  });
};

/**
 * People who clocked in on a day but aren't on any job running that day.
 * logs: time_logs (lunch + auto 8-hour placeholders are ignored)
 */
export const buildClockInRows = ({ logs = [], visits = [], jobs = [], users = [], dismissed = new Set() } = {}) => {
  const usersById = new Map(users.map((u) => [u.id, u]));
  const byDay = new Map();
  logs.forEach((log) => {
    if (log.isLunch || log.autoGenerated === true) return;
    const inAt = toDate(log.clockIn);
    if (!inAt) return;
    const day = moment(inAt).format(DAY);
    const userId = log.userId || `name:${norm(log.userName)}`;
    if (!byDay.has(day)) byDay.set(day, new Map());
    const people = byDay.get(day);
    const user = usersById.get(log.userId) || {};
    if (!people.has(userId)) {
      people.set(userId, {
        userId,
        user,
        name: personName(user) || log.userName || "Unknown",
        names: namesForPerson(user, log),
        shifts: [],
        minutes: 0,
        stillIn: false,
      });
    }
    const p = people.get(userId);
    const outAt = toDate(log.clockOut);
    const mins = outAt ? Math.max(0, (outAt - inAt) / 60000) : null;
    p.shifts.push({ inAt, outAt });
    if (mins !== null) p.minutes += mins;
    else p.stillIn = true;
  });

  const liveJobs = jobs.filter(isLiveJob);
  const out = [];
  byDay.forEach((people, day) => {
    const running = liveJobs.filter((j) => jobRunsOn(j, day));
    const dayVisits = visits.filter((v) => dayKeyOf(v.checkInAt) === day);
    people.forEach((p) => {
      const assigned = running.some((j) => techsOnDay(j, day).map(norm).some((t) => p.names.includes(t)));
      if (assigned) return;
      const key = `${p.userId}__${day}`;
      if (dismissed.has(dismissDocId("clockedIn", key))) return;
      const checkedInto = dayVisits
        .filter((v) => v.userId === p.userId || norm(v.userName) === norm(p.name))
        .map((v) => v.jobId)
        .filter(Boolean);
      // Jobs they checked in to first, then the rest running that day.
      const options = [
        ...running.filter((j) => checkedInto.includes(j.id)),
        ...running.filter((j) => !checkedInto.includes(j.id)),
      ].map((j) => ({ id: j.id, label: jobLabel(j), checkedIn: checkedInto.includes(j.id), recurring: isRecurringJob(j), job: j }));
      out.push({
        kind: "clockedIn",
        key,
        jobId: "",
        label: `${p.name} · ${moment(day, DAY).format("MMM D")}`,
        userId: p.userId,
        name: p.name,
        day,
        shifts: p.shifts,
        minutes: Math.round(p.minutes),
        stillIn: p.stillIn,
        options,
        suggestedJobId: options.find((o) => o.checkedIn)?.id || "",
      });
    });
  });
  return out.sort((a, b) => b.day.localeCompare(a.day) || a.name.localeCompare(b.name));
};

/**
 * One-time jobs where a Sub Contractor is on Tech Assign but nothing is owed
 * to them yet (same rule as Team Dashboard -> Sub Pay Setup "Needs pay set").
 * people: loadPeople() result (utils/subPayPayments)
 */
export const buildSubPayRows = ({ jobs = [], people = null, dismissed = new Set(), from = "", to = "" } = {}) => {
  if (!people) return [];
  const bucket = (n) => people.bucketKeyForName(n);
  const isSubKey = (k) => people.identities.some((i) => i.key === k);
  const today = todayKey();
  const out = [];
  jobs.forEach((job) => {
    if (!isLiveJob(job) || isRecurringJob(job)) return;
    const start = dayKeyOf(job.startDate);
    // Only work that has started (pay is owed once the sub has been out).
    if (!start || start > today) return;
    if (from && (dayKeyOf(job.endDate) || start) < from) return;
    if (to && start > to) return;
    const seen = new Set();
    techList(job.techAssign).forEach((t) => {
      const k = bucket(t);
      if (!k || !isSubKey(k) || seen.has(k)) return;
      seen.add(k);
      const line = (job.subcontractorPay || []).find((l) => bucket(l?.subcontractorName || l?.name || "") === k);
      const term = Object.values(job.subPayTerms || {}).find((x) => x?.name && bucket(x.name) === k) || null;
      const rolledProject = term && term.mode === "project" && term.setOnJobId && term.setOnJobId !== job.id;
      if (term || rolledProject) return;
      if (line && lineOwed(line) > 0) return;
      const key = `${job.id}__${k}`;
      if (dismissed.has(dismissDocId("subPay", key))) return;
      out.push({
        kind: "subPay",
        key,
        jobId: job.id,
        job,
        label: `${jobLabel(job)} · ${people.displayNameFor ? people.displayNameFor(t) : t}`,
        wo: job.wo || "",
        siteName: job.siteName || job.propertyName || "",
        serviceType: job.serviceType || "",
        status: job.status || "",
        subName: people.displayNameFor ? people.displayNameFor(t) : t,
        day: start,
        endDay: dayKeyOf(job.endDate) && dayKeyOf(job.endDate) !== start ? dayKeyOf(job.endDate) : "",
      });
    });
  });
  return out.sort((a, b) => b.day.localeCompare(a.day));
};

/* ------------------------------------------------------------------ */
/* loading                                                              */
/* ------------------------------------------------------------------ */

/** time_logs + job_visits with a clock-in between two days (inclusive). */
export const loadClockData = async (fromKey, toKeyValue) => {
  const start = moment(fromKey, DAY).startOf("day").toDate();
  const end = moment(toKeyValue, DAY).endOf("day").toDate();
  const [logSnap, visitSnap] = await Promise.all([
    getDocs(query(collection(db, "time_logs"), where("clockIn", ">=", start), where("clockIn", "<=", end))),
    getDocs(query(collection(db, "job_visits"), where("checkInAt", ">=", start), where("checkInAt", "<=", end))).catch(() => ({ docs: [] })),
  ]);
  return {
    logs: logSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
    visits: visitSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
};

/* ------------------------------------------------------------------ */
/* actions                                                              */
/* ------------------------------------------------------------------ */

/** Add names to a job's Tech Assign (whole job / whole series). Returns the new list. */
export const assignTechsToJob = async (jobId, names = []) => {
  const clean = names.map((n) => String(n || "").trim()).filter(Boolean);
  if (!clean.length) return null;
  const ref = doc(db, "jobs", jobId);
  const snap = await getDoc(ref);
  const current = snap.exists() ? snap.data() : {};
  // Older jobs store techAssign as a string; arrayUnion needs an array.
  if (current.techAssign && !Array.isArray(current.techAssign)) {
    const next = Array.from(new Set([...techList(current.techAssign), ...clean]));
    await updateDoc(ref, { techAssign: next });
    return next;
  }
  await updateDoc(ref, { techAssign: arrayUnion(...clean) });
  return Array.from(new Set([...techList(current.techAssign), ...clean]));
};

/**
 * Put one person on a job for one day.
 *   one-time job  -> added to the job's Tech Assign
 *   recurring job -> added to that visit only (occurrenceOverrides[day]),
 *                    so the rest of the series is unchanged
 * Returns a patch for local state: { id, patch }.
 */
export const assignPersonForDay = async (job, name, day) => {
  if (!isRecurringJob(job)) {
    const next = await assignTechsToJob(job.id, [name]);
    return { id: job.id, patch: { techAssign: next } };
  }
  const ref = doc(db, "jobs", job.id);
  const snap = await getDoc(ref);
  const current = snap.exists() ? snap.data() : job;
  const existing = current.occurrenceOverrides?.[day] || null;
  const base = existing && existing.techAssign !== undefined ? techList(existing.techAssign) : techList(current.techAssign);
  const nextTechs = Array.from(new Set([...base, name]));
  if (existing) {
    await updateDoc(ref, new FieldPath("occurrenceOverrides", day, "techAssign"), nextTechs);
  } else {
    await updateDoc(ref, new FieldPath("occurrenceOverrides", day), {
      techAssign: nextTechs,
      originalOccurrenceDate: day,
      occurrenceDate: day,
      startDate: day,
      updatedAt: new Date().toISOString(),
    });
  }
  const occurrenceOverrides = {
    ...(current.occurrenceOverrides || {}),
    [day]: { ...(existing || { originalOccurrenceDate: day, occurrenceDate: day, startDate: day }), techAssign: nextTechs },
  };
  return { id: job.id, patch: { occurrenceOverrides } };
};

/* ------------------------------------------------------------------ */
/* "Later" snooze (per person, this browser, until tomorrow)            */
/* ------------------------------------------------------------------ */

const snoozeKey = (uid, kind) => `jobReminderSnooze_${kind}_${uid || "anon"}`;

export const readReminderSnooze = (uid, kind) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(snoozeKey(uid, kind)) || "null");
    if (!parsed || parsed.date !== todayKey()) return { date: todayKey(), all: false, keys: [] };
    return { date: parsed.date, all: Boolean(parsed.all), keys: Array.isArray(parsed.keys) ? parsed.keys : [] };
  } catch {
    return { date: todayKey(), all: false, keys: [] };
  }
};

const writeReminderSnooze = (uid, kind, value) => {
  try {
    localStorage.setItem(snoozeKey(uid, kind), JSON.stringify(value));
  } catch {
    /* storage blocked - snooze just won't persist */
  }
  return value;
};

export const snoozeReminderAll = (uid, kind) => writeReminderSnooze(uid, kind, { ...readReminderSnooze(uid, kind), all: true });
export const snoozeReminderKeys = (uid, kind, keys = []) => {
  const cur = readReminderSnooze(uid, kind);
  return writeReminderSnooze(uid, kind, { ...cur, keys: Array.from(new Set([...cur.keys, ...keys])) });
};
